import { useEffect } from 'react';
import _ from 'lodash';
import { WebsocketProvider } from 'y-websocket';

import { getHashedIndex } from '@lyearn/core/utils/hashCode';
import { useUserContext } from '@/modules/auth/hooks/useUser';
import { UserProfile } from '@/modules/auth/types';

export type ActiveUser = Pick<UserProfile, 'id' | 'name' | 'profilePicture'> & {
  color: string;
  meta?: Record<string, string>;
};

const OPTION_COLORS = [
  { color: '#572FC7', textColor: '#fff' },
  { color: '#3948D9', textColor: '#fff' },
  { color: '#1BAE7E', textColor: '#fff' },
  { color: '#FFD260', textColor: '#000' },
  { color: '#F48749', textColor: '#fff' },
  { color: '#E75046', textColor: '#fff' },
  { color: '#E64B82', textColor: '#fff' },
  { color: '#DCD2CF', textColor: '#000' },
];

const getRandomColor = (seed: string) => {
  const index = getHashedIndex(seed, OPTION_COLORS.length);
  return OPTION_COLORS[index];
};

const getAwarenessUsers = (awareness?: WebsocketProvider['awareness']) =>
  _.uniqBy(
    _.map(Object.fromEntries(awareness?.getStates() ?? []), 'user').filter((v) => v?.id),
    'id',
  );

const useUserAwareness = (
  yProvider?: WebsocketProvider,
  onActiveUserChange?: (users: ActiveUser[]) => void,
  userMeta?: Record<string, string>,
) => {
  const { id, name, profilePicture } = useUserContext();

  useEffect(() => {
    yProvider?.awareness.setLocalStateField(
      'user',
      _.pickBy({
        ...getRandomColor(id),
        id,
        profilePicture,
        name,
        meta: userMeta,
      }),
    );
  }, [id, name, profilePicture, userMeta, yProvider?.awareness]);

  useEffect(() => {
    const awarenessListener = () => onActiveUserChange?.(getAwarenessUsers(yProvider?.awareness));

    awarenessListener();
    yProvider?.awareness.on('change', awarenessListener);
    return () => yProvider?.awareness.off('change', awarenessListener);
  }, [onActiveUserChange, yProvider?.awareness, userMeta]);
};

export default useUserAwareness;
